import React, { useEffect, useState } from "react";
import styles from "./AllProjects.module.scss";
import { ProgressDisplay, Typography, useProgress } from "@factorearth/component-library";
import { useTheme } from "hooks/useSingleton";
import { FiSearch, FiPlus, FiCalendar } from "react-icons/fi";
import { FaSortAmountUpAlt, FaSortAmountDown } from "react-icons/fa";
import ProjectGroupList from "./ProjectGroupList/ProjectGroupList";
import { Button, OptionsDropdown, Category } from "@factorearth/component-library";
import CreateNewProject from "components/CreateNewProject/CreateNewProject";
import { User, ProjectForResolver, Project, FormTemplate, FormGroup } from "db/definitions";

interface AllProjectsProps {
	projects: Project[];
	userDoc: User | undefined | null;
	createProject: (proj: Pick<ProjectForResolver, "name" | "description">) => Promise<Project | undefined>;
	online: boolean;
	formTemplates: FormTemplate[];
	updateProjectsSyncMenu: (project: Project) => void;
	addFormGroupsInState: (formGroups: FormGroup[]) => void;
}

interface GroupedProjects {
	[key: string]: Project[];
}

const AllProjects = (props: AllProjectsProps) => {
	const { projects, userDoc, createProject, online, formTemplates, addFormGroupsInState, updateProjectsSyncMenu } = props;
	const { progress, setProgress } = useProgress();
	const [createProjectOpen, setCreateProjectOpen] = useState(false);
	const color = useTheme();

	const [groupedProjects, setGroupedProjects] = useState<GroupedProjects>({});

	const [searchTerm, setSearchTerm] = useState("");
	const [numSort, setNumSort] = useState({
		ascending: false,
		descending: false,
		dateCreated: false,
	});

	useEffect(() => {
		// let projectsCopy = projects;

		// if (online) {
		//     projectsCopy = projects.filter((proj) => !proj.offline);
		// }

		const matches = projects.filter((project) => project.name.toLowerCase().includes(searchTerm.toLowerCase()));

		if (numSort.ascending) {
			const alphaSorted = matches.slice().sort((a, b) => {
				return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
			});
			setGroupedProjects(sortProjectsByGroup(alphaSorted));
		} else if (numSort.descending) {
			const alphaReverseSorted = matches.slice().sort((a, b) => {
				return b.name.localeCompare(a.name, undefined, { sensitivity: "base" });
			});
			setGroupedProjects(sortProjectsByGroup(alphaReverseSorted));
		} else if (numSort.dateCreated) {
			const dateSorted = matches.slice().sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

			setGroupedProjects(sortProjectsByGroup(dateSorted));
		} else {
			setGroupedProjects(sortProjectsByGroup(matches));
		}
	}, [searchTerm, projects, numSort]);

	let optionsObj: Record<string, Category> = {
		Sort: {
			icon: numSort.dateCreated ? <FiCalendar /> : numSort.descending ? <FaSortAmountDown /> : <FaSortAmountUpAlt />,
			title: numSort.dateCreated ? "Date" : numSort.descending ? "Z-A" : numSort.ascending ? "A-Z" : "Sort",
			options: [
				{
					name: "A-Z Ascending",
					onClick: () =>
						setNumSort({
							ascending: true,
							descending: false,
							dateCreated: false,
						}),
					icon: <FaSortAmountUpAlt />,
				},
				{
					name: "Z-A Descending",
					onClick: () =>
						setNumSort({
							ascending: false,
							descending: true,
							dateCreated: false,
						}),
					icon: <FaSortAmountDown />,
				},
				{
					name: "Date Created",
					onClick: () =>
						setNumSort({
							ascending: false,
							descending: false,
							dateCreated: true,
						}),
					icon: <FiCalendar />,
				},
			],
		},
	};

	const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(e.target.value);
	};

	const sortProjectsByGroup = (projects: Project[]) => {
		return projects.reduce((acc: GroupedProjects, project) => {
			const group = project.group || "Ungrouped";
			if (!acc[group]) {
				acc[group] = [];
			}
			acc[group].push(project);
			return acc;
		}, {} as GroupedProjects);
	};

	const buildAllProjectsList = (groupedProjects: GroupedProjects) => {
		const groups = Object.keys(groupedProjects);

		if (groups.length === 0) {
			return (
				<Typography variant={"Heading 3"} textColor={color.text.primary} style={{ paddingLeft: "24px" }}>
					No projects found
				</Typography>
			);
		}

		return groups.map((group) => (
			<ProjectGroupList
				key={group}
				groupedProject={groupedProjects[group]}
				group={group}
				numOfGroups={Object.keys(groupedProjects).length}
				online={online}
				user={userDoc}
				updateProjectsSyncMenu={updateProjectsSyncMenu}
				addFormGroupsInState={addFormGroupsInState}
				setProgress={setProgress}
			/>
		));
	};

	return (
		<>
			{progress.show && <ProgressDisplay colors={color} progress={progress} setProgress={setProgress} />}
			<div className={styles.allProjectsContainer}>
				<div className={styles.actionBarAndSearch}>
					<div className={styles.searchWrapper} style={{ border: `solid 1px ${color.background.secondary}`, background: color.background.primary }}>
						<input
							className={styles.searchInput}
							type="text"
							placeholder="Search..."
							value={searchTerm}
							onChange={handleSearch}
							style={{
								color: color.text.primary,
							}}
						/>
						<div className={styles.search_icon} style={{ color: color.text.primary }}>
							<FiSearch />
						</div>
					</div>
					<div className={styles.actionBar}>
						{userDoc && (
							<Button variant="filled" icon={<FiPlus />} colorPalette={color} onClick={() => setCreateProjectOpen(true)} label="New Project" />
						)}
						{userDoc && (
							<CreateNewProject
								group={userDoc.group}
								handleCreateProject={createProject}
								projects={projects}
								online={online}
								formTemplates={formTemplates}
								open={createProjectOpen}
								setOpen={setCreateProjectOpen}
							/>
						)}
						<OptionsDropdown colorPalette={color} optionsObj={optionsObj} />
					</div>
				</div>
				{buildAllProjectsList(groupedProjects)}
			</div>
		</>
	);
};

export default AllProjects;
